import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getSurveysCampaignsCall, getTemplatesCall } from '../../state';

type Props = {
  selectedUsersIds?: string[];
  inviteAll?: boolean;
  totalToInvite?: number;
  onCustomClose?: (openTrigger: boolean) => void;
  selectedTemplate?: string;
  isExistingCampaign?: boolean;
  currentCampaignId?: string;
};

export const StepLastConfirm: FC<Props> = ({
  selectedUsersIds,
  inviteAll = false,
  totalToInvite,
  onCustomClose,
  selectedTemplate,
  isExistingCampaign,
  currentCampaignId,
}) => {
  const t = useTranslations('surveys.sendSurvey.stepLastConfirm.sent');
  const navigate = useNavigate();

  const { genericResponsiveValue } = useResponsiveStyle();

  const { surveyId } = useParams();

  const { response: responseGetTemplates } = getTemplatesCall.hook();
  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  const goToVisibilitySettings = useCallback(() => {
    if (selectedTemplate !== '') {
      const templateId = responseGetTemplates?.find(
        template => template.name === selectedTemplate,
      )?.id;
      if (templateId) {
        navigate(`${Routes.SurveyDetail.replace(':surveyId', templateId)}`, {
          state: { dialog: 'visibility' },
        });
      }
    }
    if (surveyId) {
      navigate(`${Routes.SurveyDetail.replace(':surveyId', surveyId)}`, {
        state: { dialog: 'visibility' },
      });
    }
    if (onCustomClose) {
      onCustomClose(true);
    }
  }, [navigate, onCustomClose, responseGetTemplates, selectedTemplate, surveyId]);

  const totalCount = inviteAll ? totalToInvite : selectedUsersIds?.length || 0;

  const templateName = useMemo(() => {
    if (selectedTemplate !== '') {
      return selectedTemplate;
    }

    const tempName =
      responseGetTemplates && responseGetTemplates.find(template => template.id === surveyId)?.name;

    return tempName;
  }, [responseGetTemplates, selectedTemplate, surveyId]);

  const currentVisibility = useMemo(() => {
    if (selectedTemplate !== '') {
      const templateId = responseGetTemplates?.find(
        template => template.name === selectedTemplate,
      )?.id;
      const campaign = responseGetSurveysCampaigns?.findLast(
        currentCampaign => currentCampaign.surveyTemplateId === templateId,
      );
      return campaign?.isVisible;
    }
    if (isExistingCampaign && currentCampaignId) {
      const campaign = responseGetSurveysCampaigns?.find(
        currentCampaign => currentCampaign.id === currentCampaignId,
      );
      return campaign?.isVisible;
    }

    return false;
  }, [
    selectedTemplate,
    isExistingCampaign,
    currentCampaignId,
    responseGetTemplates,
    responseGetSurveysCampaigns,
  ]);

  useEffect(() => {
    const dialogTitle = document.getElementById('dialog-send-survey-content-title');
    if (dialogTitle) {
      dialogTitle.style.display = 'none';
    }
  }, []);

  return (
    <Box mb='xs' mt='m' width='100%'>
      <Box textAlign='center'>
        <Svg name='rocket' size='s' />
      </Box>
      <Typography mt='xs' textAlign='center' variant='h2'>
        {t('title')}
      </Typography>
      <Typography mt='s' textAlign='center' variant='p3/light'>
        {t(totalCount === 1 ? 'description.single' : 'description.plural', {
          templateName,
          count: totalCount,
        })}
      </Typography>
      <Card
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        mt='m'
        alignItems={genericResponsiveValue({
          breakPoints: { desktop: 'center' },
          defaultValue: 'flex-start',
        })}
        backgroundColor='primary'
        display='flex'
      >
        <Box
          mr={genericResponsiveValue({
            breakPoints: { desktop: 'xs' },
            defaultValue: 'none',
          })}
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
        >
          <Svg name={currentVisibility ? 'eyeOn' : 'eyeOff'} size='m' />
        </Box>
        <Box flex='2'>
          <Typography variant='h4'>
            {currentVisibility ? t('isVisible.title') : t('visibility.title')}
          </Typography>
          <Typography variant='p2/light'>
            {currentVisibility ? t('isVisible.description') : t('visibility.description')}
          </Typography>
        </Box>
        <Box
          flex='1'
          mt={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
        >
          <Button
            style={{ margin: '0 0 0 auto' }}
            onClick={goToVisibilitySettings}
            variant='positive'
          >
            {t('visibility.buttonLabel')}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};
